import { t } from '@lingui/core/macro'
import { useMemo, useState } from 'react'
import { Tab, Tabs } from 'react-bootstrap'
import ECharts from '~/components/echarts'
import FootNote from '~/components/footnote'
import AWSEC2DashboardCommitmentCoverageFilters from '~/dashboard/aws_ec2_dashboard_commitment_coverage_filters'
import AWSEC2DashboardDailySpendFilters from '~/dashboard/aws_ec2_dashboard_daily_spend_filters'
import AWSEC2DashboardInventoryFilters from '~/dashboard/aws_ec2_dashboard_inventory_filters'
import AWSEC2DashboardMainSpendFilters from '~/dashboard/aws_ec2_dashboard_main_spend_filters'
import AWSEC2SbuUtilizationTab from '~/dashboard/aws_ec2_sbu_utilization_tab'
import {
  awsEC2CommitmentWasteVsOnDemandMonthlyChartPath,
  awsEC2DailySpendByOnDemandChartPath,
  awsEC2DailySpendByPurchaseTypeChartPath,
  awsEC2InstancesByInstanceStateChartPath,
  awsEC2InstancesByInstanceTypeChartPath,
  awsEC2InstancesByTenancyChartPath,
  awsEC2InstancesByUsageOperationChartPath,
  awsEC2MonthlySpendByRecordTypeChartPath,
  awsEC2ReservationsEndingEachMonthChartPath,
  awsEC2SavingsPlansEndingEachMonthChartPath,
  awsEC2UnutilizedCommitmentsMonthlyChartPath,
  awsMonthlyEC2AmortizedRunningByPurchaseTypeChartPath,
  awsMonthlyEC2RunningByOperatingSystemChartPath,
  awsMonthlyEC2RunningByPurchaseTypeChartPath,
  awsMonthlyEC2SavingsPercentageChartPath,
  awsPreviousMonthEC2SpendByInstanceTypeChartPath,
  awsPreviousMonthEC2SpendByOperatingSystemChartPath,
} from '~/routes'

export default function AWSEC2DashboardCharts({ organizationId: orgId, isAdmin }) {
  const [mainParams, setMainParams] = useState({})
  const [InventoryParams, setInventoryParams] = useState({})
  const [dailySpendParams, setDailySpendParams] = useState({})
  const [commitmentCoverageParams, setCommitmentCoverageParams] = useState({})

  const [openedTabs, setOpenedTabs] = useState(['main-tab'])
  const [activeTab, setActiveTab] = useState('main-tab')

  const handleTabChange = (tabName) => {
    setActiveTab(tabName)

    // Lazy load charts
    if (!openedTabs.includes(tabName)) {
      setOpenedTabs((prevOpenedTabs) => [...prevOpenedTabs, tabName])
    }
  }

  const openedTab = (tabName) => {
    return openedTabs.includes(tabName)
  }

  // memoize URLs to prevent rerenders
  const awsMonthlyEC2RunningByPurchaseTypeChartUrl = useMemo(
    () => awsMonthlyEC2RunningByPurchaseTypeChartPath(orgId),
    [orgId],
  )
  const awsMonthlyEC2RunningByOperatingSystemChartUrl = useMemo(
    () => awsMonthlyEC2RunningByOperatingSystemChartPath(orgId),
    [orgId],
  )
  const awsPreviousMonthEC2SpendByInstanceTypeChartUrl = useMemo(
    () => awsPreviousMonthEC2SpendByInstanceTypeChartPath(orgId),
    [orgId],
  )
  const awsPreviousMonthEC2SpendByOperatingSystemChartUrl = useMemo(
    () => awsPreviousMonthEC2SpendByOperatingSystemChartPath(orgId),
    [orgId],
  )
  const awsEC2InstancesByInstanceTypeChartChartUrl = useMemo(
    () => awsEC2InstancesByInstanceTypeChartPath(orgId),
    [orgId],
  )
  const awsEC2InstancesByTenancyChartChartUrl = useMemo(() => awsEC2InstancesByTenancyChartPath(orgId), [orgId])
  const awsEC2InstancesByUsageOperationChartChartUrl = useMemo(
    () => awsEC2InstancesByUsageOperationChartPath(orgId),
    [orgId],
  )
  const awsEC2MonthlySpendByRecordTypeChartUrl = useMemo(() => awsEC2MonthlySpendByRecordTypeChartPath(orgId), [orgId])
  const awsEC2InstancesByInstanceStateChartChartUrl = useMemo(
    () => awsEC2InstancesByInstanceStateChartPath(orgId),
    [orgId],
  )
  const awsEC2DailySpendByOnDemandChartUrl = useMemo(() => awsEC2DailySpendByOnDemandChartPath(orgId), [orgId])
  const awsEC2DailySpendByPurchaseTypeChartUrl = useMemo(() => awsEC2DailySpendByPurchaseTypeChartPath(orgId), [orgId])
  const awsEC2ReservationsEndingEachMonthChartUrl = useMemo(
    () => awsEC2ReservationsEndingEachMonthChartPath(orgId),
    [orgId],
  )
  const awsEC2SavingsPlansEndingEachMonthChartUrl = useMemo(
    () => awsEC2SavingsPlansEndingEachMonthChartPath(orgId),
    [orgId],
  )
  const awsMonthlyEC2SavingsPercentageChartUrl = useMemo(() => awsMonthlyEC2SavingsPercentageChartPath(orgId), [orgId])
  const awsEC2CommitmentWasteVsOnDemandMonthlyChartUrl = useMemo(
    () => awsEC2CommitmentWasteVsOnDemandMonthlyChartPath(orgId),
    [orgId],
  )
  const awsMonthlyEC2AmortizedRunningByPurchaseTypeChartUrl = useMemo(
    () => awsMonthlyEC2AmortizedRunningByPurchaseTypeChartPath(orgId),
    [orgId],
  )
  const awsEC2UnutilizedCommitmentsMonthlyChartUrl = useMemo(
    () => awsEC2UnutilizedCommitmentsMonthlyChartPath(orgId),
    [orgId],
  )

  return (
    <div className='card-tabs'>
      <Tabs id='controlled-tabs' activeKey={activeTab} onSelect={(k) => handleTabChange(k)}>
        <Tab eventKey='main-tab' title={t`Main Spend`}>
          <div className='tab-content'>
            <div className='card tab-pane active show'>
              <div className='card-body'>
                <AWSEC2DashboardMainSpendFilters organizationId={orgId} setChartParams={setMainParams} />
                {openedTab('main-tab') && (
                  <div className='row gy-4'>
                    <div className='col-sm-6'>
                      <ECharts
                        title={t`Previous 6 months EC2 Instances spend by purchase type`}
                        chartInfoTitle='aws_ec2_previous_6_months_spend_by_purchase_type'
                        url={awsMonthlyEC2RunningByPurchaseTypeChartUrl}
                        params={mainParams}
                        showExportButton
                      />
                    </div>
                    <div className='col-sm-6'>
                      <ECharts
                        title={t`Previous 6 months EC2 Instances spend by operating system`}
                        chartInfoTitle='aws_ec2_previous_6_months_spend_by_operating_system'
                        url={awsMonthlyEC2RunningByOperatingSystemChartUrl}
                        params={mainParams}
                        showExportButton
                      />
                    </div>
                    <div className='col-sm-6'>
                      <ECharts
                        title={t`Previous Month EC2 Top Instance Types for total Spend`}
                        chartInfoTitle='aws_ec2_previous_month_top_instance_types_spend'
                        url={awsPreviousMonthEC2SpendByInstanceTypeChartUrl}
                        params={mainParams}
                      />
                    </div>
                    <div className='col-sm-6'>
                      <ECharts
                        title={t`Previous Month EC2 Top Operating Systems for total Spend`}
                        chartInfoTitle='aws_ec2_previous_month_top_operating_systems_spend'
                        url={awsPreviousMonthEC2SpendByOperatingSystemChartUrl}
                        params={mainParams}
                      />
                    </div>
                    <div className='col-sm-6'>
                      <ECharts
                        title={t`Previous 12 months EC2 Instances Spend by Record Type`}
                        chartInfoTitle='aws_ec2_previous_12_months_spend_by_record_type'
                        url={awsEC2MonthlySpendByRecordTypeChartUrl}
                        params={mainParams}
                        showExportButton
                      />
                    </div>
                  </div>
                )}
                <div className='row'>
                  <FootNote />
                </div>
              </div>
            </div>
          </div>
        </Tab>
        <Tab eventKey='inventory-tab' title={t`Inventory`}>
          <div className='tab-content'>
            <div className='card tab-pane active show'>
              <div className='card-body'>
                <AWSEC2DashboardInventoryFilters organizationId={orgId} setChartParams={setInventoryParams} />
                {openedTab('inventory-tab') && (
                  <div className='row gy-4'>
                    <div className='col-sm-6'>
                      <ECharts
                        title={t`Quantity of EC2 Instances by Instance Type`}
                        chartInfoTitle='aws_ec2_quantity_by_instance_type'
                        url={awsEC2InstancesByInstanceTypeChartChartUrl}
                        params={InventoryParams}
                      />
                    </div>
                    <div className='col-sm-6'>
                      <ECharts
                        title={t`Quantity of EC2 Instances by Tenancy`}
                        chartInfoTitle='aws_ec2_quantity_by_tenancy'
                        url={awsEC2InstancesByTenancyChartChartUrl}
                        params={InventoryParams}
                      />
                    </div>
                    <div className='col-sm-6'>
                      <ECharts
                        title={t`Quantity of EC2 Instances by Usage Operation`}
                        chartInfoTitle='aws_ec2_quantity_by_usage_operation'
                        url={awsEC2InstancesByUsageOperationChartChartUrl}
                        params={InventoryParams}
                      />
                    </div>
                    <div className='col-sm-6'>
                      <ECharts
                        title={t`Quantity of EC2 Instances by Instance State`}
                        chartInfoTitle='aws_ec2_quantity_by_instance_state'
                        url={awsEC2InstancesByInstanceStateChartChartUrl}
                        params={InventoryParams}
                      />
                    </div>
                  </div>
                )}
                <div className='row'>
                  <FootNote />
                </div>
              </div>
            </div>
          </div>
        </Tab>
        <Tab eventKey='commitment-coverage-tab' title={t`Commitments`}>
          <div className='tab-content'>
            <div className='card tab-pane active show'>
              <div className='card-body'>
                <AWSEC2DashboardCommitmentCoverageFilters
                  organizationId={orgId}
                  setChartParams={setCommitmentCoverageParams}
                />
                {openedTab('commitment-coverage-tab') && (
                  <>
                    <div className='row gy-4'>
                      <div className='col-sm-6'>
                        <ECharts
                          title={t`Previous 6 months EC2 Instances spend On Demand Equivalent`}
                          chartInfoTitle='aws_ec2_previous_6_months_spend_by_purchase_type'
                          url={awsMonthlyEC2AmortizedRunningByPurchaseTypeChartUrl}
                          params={commitmentCoverageParams}
                          showExportButton={false}
                        />
                      </div>
                      <div className='col-sm-6'>
                        <ECharts
                          title={t`Unutilized Commitments by Month`}
                          chartInfoTitle='aws_ec2_unutilized_commitments_monthly'
                          url={awsEC2UnutilizedCommitmentsMonthlyChartUrl}
                          params={commitmentCoverageParams}
                          showExportButton={false}
                        />
                      </div>
                    </div>
                    <div className='row gy-4'>
                      <div className='col-sm-6'>
                        <ECharts
                          title={t`Unutilized Commitments vs On-Demand Equivalent`}
                          chartInfoTitle='aws_ec2_commitment_waste_vs_on_demand_monthly'
                          url={awsEC2CommitmentWasteVsOnDemandMonthlyChartUrl}
                          params={commitmentCoverageParams}
                          showExportButton={false}
                        />
                      </div>
                      <div className='col-sm-6'>
                        <ECharts
                          title={t`EC2 Instances Monthly Savings Percentage`}
                          chartInfoTitle='aws_ec2_instances_monthly_savings_percentage'
                          url={awsMonthlyEC2SavingsPercentageChartUrl}
                          params={commitmentCoverageParams}
                        />
                      </div>
                    </div>
                    <div className='row gy-4'>
                      <div className='col-sm-6'>
                        <ECharts
                          title={t`Value of EC2 Reservations Ending in Each Month`}
                          chartInfoTitle='aws_ec2_reservations_ending_each_month_value'
                          url={awsEC2ReservationsEndingEachMonthChartUrl}
                          params={commitmentCoverageParams}
                          showExportButton
                        />
                      </div>
                      <div className='col-sm-6'>
                        <ECharts
                          title={t`Value of Savings plans Ending in Each Month`}
                          chartInfoTitle='aws_ec2_savings_plans_ending_each_month_value'
                          url={awsEC2SavingsPlansEndingEachMonthChartUrl}
                          params={commitmentCoverageParams}
                          showExportButton
                        />
                      </div>
                    </div>
                  </>
                )}
                <div className='row'>
                  <FootNote showServiceFilters={false} />
                </div>
              </div>
            </div>
          </div>
        </Tab>
        <Tab eventKey='daily-tab' title={t`Daily Spend`}>
          <div className='tab-content'>
            <div className='card tab-pane active show'>
              <div className='card-body'>
                <AWSEC2DashboardDailySpendFilters organizationId={orgId} setChartParams={setDailySpendParams} />
                {openedTab('daily-tab') && (
                  <div className='row gy-4'>
                    <div className='col-sm-6'>
                      <ECharts
                        title={t`Previous 30 Days Usage for On-Demand EC2 Instances *`}
                        chartInfoTitle='aws_ec2_previous_30_days_usage_on_demand'
                        url={awsEC2DailySpendByOnDemandChartUrl}
                        params={dailySpendParams}
                        showExportButton
                      />
                    </div>
                    <div className='col-sm-6'>
                      <ECharts
                        title={t`Previous 30 Days Usage for EC2 Instances by Purchase Type *`}
                        chartInfoTitle='aws_ec2_previous_30_days_usage_by_purchase_type'
                        url={awsEC2DailySpendByPurchaseTypeChartUrl}
                        params={dailySpendParams}
                        showExportButton
                      />
                    </div>
                  </div>
                )}
                <div className='row'>
                  <FootNote />
                </div>
              </div>
            </div>
          </div>
        </Tab>
        {isAdmin && (
          <Tab eventKey='sbu-utilization-tab' title={t`SBU Utilization`}>
            <div className='tab-content'>
              <div className='card tab-pane active show'>
                <div className='card-body'>
                  {openedTab('sbu-utilization-tab') && <AWSEC2SbuUtilizationTab organizationId={orgId} />}
                </div>
              </div>
            </div>
          </Tab>
        )}
      </Tabs>
    </div>
  )
}
